import type { Contact } from "./types.ts";
import { CRLF, foldLine } from "../shared/line-fold.ts";

/**
 * Renders `contacts/contacts.vcf` (vCard 3.0) and `contacts/contacts.csv`
 * (ARCHITECTURE.md §7) from a list of contacts already captured to disk.
 *
 * Pure and dependency-free, unlike export.ts — kept separate so it is
 * unit-testable under plain Node, the same pattern every other module here
 * follows for logic that doesn't touch Graph/auth.
 */

/** Backslash-escapes vCard's reserved characters (RFC 6350 §3.4). */
function escapeVCardValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");
}

function contactName(contact: Contact): { family: string; given: string; full: string } {
    const family = contact.surname ?? "";
    const given = contact.givenName ?? "";
    const full = contact.displayName || [given, family].filter(Boolean).join(" ") || "Unnamed";
    return { family, given, full };
}

/** One VCARD block, CRLF-terminated per line, spec-folded. */
export function renderVCard(contact: Contact): string {
    const { family, given, full } = contactName(contact);
    const lines: string[] = ["BEGIN:VCARD", "VERSION:3.0"];

    lines.push(`N:${escapeVCardValue(family)};${escapeVCardValue(given)};;;`);
    lines.push(`FN:${escapeVCardValue(full)}`);

    for (const email of contact.emailAddresses ?? []) {
        if (email.address) {
            lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardValue(email.address)}`);
        }
    }
    for (const phone of contact.businessPhones ?? []) {
        lines.push(`TEL;TYPE=WORK,VOICE:${escapeVCardValue(phone)}`);
    }
    for (const phone of contact.homePhones ?? []) {
        lines.push(`TEL;TYPE=HOME,VOICE:${escapeVCardValue(phone)}`);
    }
    if (contact.mobilePhone) {
        lines.push(`TEL;TYPE=CELL,VOICE:${escapeVCardValue(contact.mobilePhone)}`);
    }
    if (contact.companyName) {
        lines.push(`ORG:${escapeVCardValue(contact.companyName)}`);
    }
    if (contact.jobTitle) {
        lines.push(`TITLE:${escapeVCardValue(contact.jobTitle)}`);
    }

    lines.push("END:VCARD");
    return lines.map(foldLine).join(CRLF) + CRLF;
}

/** Sorted by display name, for a stable diff-friendly file across repeat runs. */
export function renderVCardFile(contacts: readonly Contact[]): string {
    const sorted = [...contacts].sort((a, b) =>
        contactName(a).full.localeCompare(contactName(b).full)
    );
    return sorted.map(renderVCard).join("");
}

const CSV_HEADER = [
    "Name",
    "Given Name",
    "Surname",
    "Email",
    "Business Phone",
    "Home Phone",
    "Mobile Phone",
    "Company",
    "Job Title"
];

/** RFC 4180: quote a field if it contains a comma, quote or newline; double up embedded quotes. */
function escapeCsvField(value: string): string {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

function contactCsvRow(contact: Contact): string[] {
    const { family, given, full } = contactName(contact);
    return [
        full,
        given,
        family,
        contact.emailAddresses?.[0]?.address ?? "",
        contact.businessPhones?.[0] ?? "",
        contact.homePhones?.[0] ?? "",
        contact.mobilePhone ?? "",
        contact.companyName ?? "",
        contact.jobTitle ?? ""
    ];
}

/**
 * Only the first email/phone of each kind — CSV is the flat, spreadsheet-
 * friendly companion to the vCard file, which carries every value in full.
 */
export function renderCsvFile(contacts: readonly Contact[]): string {
    const sorted = [...contacts].sort((a, b) =>
        contactName(a).full.localeCompare(contactName(b).full)
    );
    const rows = [CSV_HEADER, ...sorted.map(contactCsvRow)];
    return rows.map((row) => row.map(escapeCsvField).join(",")).join("\r\n") + "\r\n";
}
